import { useId, useRef } from 'react'
import type { KeyboardEvent } from 'react'
import { cx } from './internal/cx'
import './SegmentedControl.css'

export type SegmentedControlOption = {
  value: string
  label: string
}

export type SegmentedControlProps = {
  label?: string
  value: string
  onChange: (value: string) => void
  options: SegmentedControlOption[]
}

/**
 * Radio-group semantics with roving tabindex; arrow keys move and select,
 * matching native radio behaviour (spec §10.4 keyboard operability).
 */
export function SegmentedControl({ label, value, onChange, options }: SegmentedControlProps) {
  const labelId = useId()
  const groupRef = useRef<HTMLDivElement>(null)

  const moveSelection = (currentIndex: number, delta: number) => {
    const nextIndex = (currentIndex + delta + options.length) % options.length
    const next = options[nextIndex]
    if (!next) return
    onChange(next.value)
    const buttons = groupRef.current?.querySelectorAll<HTMLButtonElement>('[role="radio"]')
    buttons?.[nextIndex]?.focus()
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const currentIndex = options.findIndex((option) => option.value === value)
    if (currentIndex === -1) return
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault()
      moveSelection(currentIndex, 1)
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault()
      moveSelection(currentIndex, -1)
    }
  }

  return (
    <div className="ui-segmented-field">
      {label && (
        <span className="ui-segmented-field__label" id={labelId}>
          {label}
        </span>
      )}
      <div
        className="ui-segmented"
        role="radiogroup"
        aria-labelledby={label ? labelId : undefined}
        aria-label={label ? undefined : 'Keuze'}
        ref={groupRef}
        onKeyDown={handleKeyDown}
      >
        {options.map((option) => {
          const selected = option.value === value
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={selected}
              tabIndex={selected ? 0 : -1}
              className={cx('ui-segmented__option', selected && 'ui-segmented__option--selected')}
              onClick={() => onChange(option.value)}
            >
              {option.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}
